import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

function Dialog({
  open,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  // Track previous open state to detect transitions
  const prevOpenRef = React.useRef(open)

  // Fix: Blur focused element when dialog opens via controlled prop
  // This prevents aria-hidden conflict with elements in #root
  // See: https://github.com/radix-ui/primitives/issues/1241
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open

    if (open && !wasOpen) {
      // Dialog is opening - blur any focused element in #root
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    if (!open && wasOpen) {
      // Dialog is closing - force cleanup aria-hidden
      // Use timeout to run after Radix cleanup
      const timeoutId = setTimeout(() => {
        const root = document.getElementById('root')
        if (root?.hasAttribute('aria-hidden')) {
          root.removeAttribute('aria-hidden')
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [open])

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Blur any focused element to prevent aria-hidden conflicts
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      onOpenChange?.(newOpen)
      // When closing, force remove aria-hidden from #root to prevent page lock
      if (!newOpen) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const root = document.getElementById('root')
            if (root?.hasAttribute('aria-hidden')) {
              root.removeAttribute('aria-hidden')
            }
          })
        })
      }
    },
    [onOpenChange]
  )

  return <DialogPrimitive.Root data-slot="dialog" open={open} onOpenChange={handleOpenChange} {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean
  }
>(({ className, children, showCloseButton = true, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => (
  <DialogPortal data-slot="dialog-portal">
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      data-slot="dialog-content"
      className={cn(
        "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none sm:max-w-lg",
        className
      )}
      // Fix: Prevent aria-hidden conflict when dialog opens
      // Delay focus to ensure Portal is fully mounted and aria-hidden is properly set up
      // See: https://github.com/radix-ui/primitives/issues/1241
      onOpenAutoFocus={(e) => {
        // Prevent immediate focus which can conflict with aria-hidden on #root
        e.preventDefault()
        // Use requestAnimationFrame to delay focus until after Portal is mounted
        requestAnimationFrame(() => {
          // Focus the dialog content itself or let the focus trap handle it
          const content = e.currentTarget
          if (content instanceof HTMLElement) {
            content.focus()
          }
        })
        // Call custom handler if provided
        onOpenAutoFocus?.(e)
      }}
      // Fix: Prevent aria-hidden from getting stuck on #root when dialog closes
      // This happens when focus is still inside the dialog during unmount
      // See: https://github.com/radix-ui/primitives/issues/1241
      onCloseAutoFocus={(e) => {
        // Prevent default focus restoration which can conflict with nested Radix components (Select, etc.)
        e.preventDefault()
        // Blur any focused element to ensure nothing retains focus during unmount
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
        // Force remove aria-hidden from #root to prevent page lock
        const root = document.getElementById('root')
        if (root?.hasAttribute('aria-hidden')) {
          root.removeAttribute('aria-hidden')
        }
        // Call custom handler if provided
        onCloseAutoFocus?.(e)
      }}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          data-slot="dialog-close"
          className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
        >
          <XIcon />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
